import React from 'react';
import { useHardwareItems } from '../hooks/useContent';
import StorageImage from './StorageImage';

const GLASS_TYPES = [
  {
    title: 'Tempered Glass',
    items: [
      '4–5× stronger than regular glass',
      'Breaks into small, rounded pieces for safety',
      'Available in 8mm, 10mm, and 12mm',
      'Ideal for railings and shower enclosures',
    ],
  },
  {
    title: 'Laminated Glass',
    items: [
      'Multiple layers with PVB interlayer',
      'Holds together when broken for security',
      'Available in 8mm, 10mm, and 12mm',
      'Perfect for overhead and security applications',
    ],
  },
];

const CUSTOM_OPTIONS = [
  'Custom sizes up to 96" x 130"',
  'Polished or pencil edges',
  'Custom hole placement',
  'Clear or tinted options',
  'Low-iron glass available',
  'Custom notches and cutouts',
];

const Hardware = () => {
  const { data: items, isLoading } = useHardwareItems();

  const categories = items.reduce<Record<string, typeof items>>((acc, item) => {
    if (!acc[item.category]) acc[item.category] = [];
    acc[item.category].push(item);
    return acc;
  }, {});

  return (
    <section id="hardware" className="py-24 sm:py-32 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="h-px w-8 bg-gold-400" />
            <span className="section-label">Hardware & Glass</span>
            <div className="h-px w-8 bg-gold-400" />
          </div>
          <h2 className="section-title mb-4">Hardware & Glass Sizes</h2>
          <p className="text-slate-500 max-w-2xl mx-auto">
            Premium hardware components and custom glass panels for every installation requirement — tempered and laminated safety glass in 8mm, 10mm, and 12mm.
          </p>
        </div>

        {isLoading ? (
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-brand-600" />
          </div>
        ) : Object.keys(categories).length > 0 ? (
          <div className="space-y-16">
            {Object.entries(categories).map(([category, categoryItems]) => (
              <div key={category}>
                <div className="flex items-center gap-4 mb-8">
                  <h3 className="font-display text-2xl font-bold text-slate-900">{category}</h3>
                  <div className="flex-1 h-px bg-slate-200" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {categoryItems.map((item) => (
                    <div
                      key={item.id}
                      className="group glass-card overflow-hidden"
                    >
                      <div className="relative h-48 overflow-hidden rounded-t-2xl">
                        <StorageImage
                          path={item.image_path}
                          alt={item.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      </div>
                      <div className="p-5">
                        <h4 className="font-display font-bold text-slate-900 mb-1.5 group-hover:text-brand-600 transition-colors">
                          {item.title}
                        </h4>
                        <p className="text-slate-500 text-sm leading-relaxed">{item.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center text-slate-400 py-12">No hardware items configured yet.</div>
        )}

        {/* Glass Sizes */}
        <div className="mt-20">
          <div className="flex items-center gap-4 mb-10">
            <h3 className="font-display text-2xl font-bold text-slate-900">Glass Sizes & Types</h3>
            <div className="flex-1 h-px bg-slate-200" />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {GLASS_TYPES.map(({ title, items: glassItems }) => (
              <div key={title} className="glass-card p-8">
                <h4 className="font-display text-xl font-bold text-slate-900 mb-5">{title}</h4>
                <ul className="space-y-3">
                  {glassItems.map((item) => (
                    <li key={item} className="flex items-center gap-3 text-slate-600 text-sm">
                      <span className="w-1.5 h-1.5 bg-brand-500 rounded-full flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="bg-white rounded-2xl p-8 border border-slate-100">
            <h4 className="font-display text-xl font-bold text-slate-900 mb-6">Custom Options</h4>
            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {CUSTOM_OPTIONS.map((option) => (
                <li key={option} className="flex items-center gap-3 text-slate-600 text-sm">
                  <span className="w-1.5 h-1.5 bg-gold-500 rounded-full flex-shrink-0" />
                  {option}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hardware;
